import type { PaginationMeta } from '@tms/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useT } from '../../i18n';
import { Button } from './button';

export interface PaginationProps {
  meta: PaginationMeta;
  onPageChange: (page: number) => void;
}

export const Pagination = ({ meta, onPageChange }: PaginationProps) => {
  const { t } = useT();
  if (meta.totalPages <= 1) return null;

  const from = (meta.page - 1) * meta.perPage + 1;
  const to = Math.min(meta.page * meta.perPage, meta.total);

  return (
    <nav
      className="flex items-center justify-between gap-3 border-t border-border px-4 py-2.5"
      aria-label={t('common.pagination')}
    >
      <p className="text-[12px] text-ink-muted">
        {t('common.paginationRange', { from, to, total: meta.total })}
      </p>
      <div className="flex items-center gap-1.5">
        <Button
          size="sm"
          onClick={() => onPageChange(meta.page - 1)}
          disabled={meta.page <= 1}
          aria-label={t('common.previousPage')}
        >
          <ChevronLeft className="size-3.5" />
          {t('common.previous')}
        </Button>
        <span className="px-1 text-[12px] text-ink-muted">
          {t('common.paginationPage', { page: meta.page, totalPages: meta.totalPages })}
        </span>
        <Button
          size="sm"
          onClick={() => onPageChange(meta.page + 1)}
          disabled={meta.page >= meta.totalPages}
          aria-label={t('common.nextPage')}
        >
          {t('common.next')}
          <ChevronRight className="size-3.5" />
        </Button>
      </div>
    </nav>
  );
};
